import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { domainsAPI, emailAPI } from '@hpanel/web-common/api/client'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * /client/email/mailboxes/:id/password — set a new password for one mailbox.
 *
 * PUT /api/email/mailboxes/{id}/password existed but had no UI; the manual
 * could only offer a curl command. A routed page, not a modal (CLAUDE.md §15).
 */
export const MailboxPasswordPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const mailboxId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [address, setAddress] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Best-effort label; the form works without it.
    Promise.all([emailAPI.listMailboxes(), domainsAPI.list(user?.id)])
      .then(([mailboxes, d]) => {
        const mb = mailboxes.find((m) => m.id === mailboxId)
        const domain = d.domains.find((x) => x.id === mb?.domain_id)
        if (mb) setAddress(`${mb.local_part}@${domain?.name ?? '?'}`)
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[MailboxPasswordPage] mailbox lookup failed', e)
      })
  }, [mailboxId, user?.id])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 10) {
      setError('Mailbox passwords must be at least 10 characters')
      return
    }
    setBusy(true)
    try {
      await emailAPI.changeMailboxPassword(mailboxId, password)
      navigate('/client/email', {
        state: {
          notice: `Password changed for ${address ?? 'the mailbox'}. Update it in your mail apps.`,
        },
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[MailboxPasswordPage] change failed', err)
      setError(err instanceof Error ? err.message : 'Failed to change the mailbox password')
      setBusy(false)
    }
  }

  return (
    <div data-testid="mailbox-password-page" className="max-w-xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Change mailbox password</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {address ? <span className="font-mono">{address}</span> : `Mailbox #${mailboxId}`}. Mail apps using
          the old password will stop working until you update them.
        </p>
      </div>
      <form onSubmit={onSubmit} className="card p-6 space-y-4">
        {error && (
          <div
            data-testid="mailbox-password-error"
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-3 text-sm"
          >
            {error}
          </div>
        )}
        <div>
          <label htmlFor="mailbox-new-password" className="label">New mailbox password</label>
          <input
            id="mailbox-new-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">At least 10 characters.</p>
        </div>
        <div>
          <label htmlFor="mailbox-confirm-password" className="label">Confirm new mailbox password</label>
          <input
            id="mailbox-confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input w-full"
          />
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50">
            {busy ? 'Changing…' : 'Change mailbox password'}
          </button>
          <Link to="/client/email" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  )
}
